import { AuthHelper, CookieHelper, ErrorHelper } from '@helpers';

interface UploadFileResponse {
  id: number;
  name: string;
  [key: string]: any;
}

const uploadFile = async (endpoint: string, file: File, fileName?: string): Promise<UploadFileResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('file_name', fileName || file.name);

  const response = await fetch(
    `${
      process.env.NODE_ENV === 'development' ? process.env.NEXT_PUBLIC_API_BASE_PATH : window.location.origin
    }${endpoint}`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'X-CSRFToken': CookieHelper.get('csrf') || '',
        Authorization: `Bearer ${CookieHelper.get('access_token')}`,
      },
      body: formData,
    },
  );

  if (response.status === 401) {
    await AuthHelper.checkAndRefreshAccessToken(async () => {
      const retryResponse = await fetch(
        `${
          process.env.NODE_ENV === 'development' ? process.env.NEXT_PUBLIC_API_BASE_PATH : window.location.origin
        }${endpoint}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'X-CSRFToken': CookieHelper.get('csrf') || '',
            Authorization: `Bearer ${CookieHelper.get('access_token')}`,
          },
          body: formData,
        },
      );
      if (!(retryResponse.status >= 200 && retryResponse.status < 209)) {
        let responseMessage;
        try {
          const contentType = retryResponse.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            responseMessage = await retryResponse.json();
          } else {
            responseMessage = await retryResponse.text();
          }
        } catch (parseError) {
          responseMessage = 'Failed to parse error response';
        }
        const error = new Error('Failed to upload file after retry');
        (error as any).response = retryResponse;
        (error as any).body = responseMessage;
        (error as any).status = retryResponse.status;
        throw error;
      }
    });
  } else if (!(response.status >= 200 && response.status < 209)) {
    let responseMessage;
    try {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        responseMessage = await response.json();
      } else {
        responseMessage = await response.text();
      }
    } catch (parseError) {
      responseMessage = 'Failed to parse error response';
    }
    const error = new Error('Failed to upload file');
    (error as any).response = response;
    (error as any).body = responseMessage;
    (error as any).status = response.status;
    throw error;
  }

  return response.json();
};

const submitFormData = async <T = any>(endpoint: string, formData: FormData, method: string = 'POST'): Promise<T> => {
  try {
    const response = await fetch(
      `${
        process.env.NODE_ENV === 'development' ? process.env.NEXT_PUBLIC_API_BASE_PATH : window.location.origin
      }${endpoint}`,
      {
        method,
        credentials: 'include',
        headers: {
          'X-CSRFToken': CookieHelper.get('csrf') || '',
          Authorization: `Bearer ${CookieHelper.get('access_token')}`,
        },
        body: formData,
      },
    );

    if (response.status === 401) {
      await AuthHelper.checkAndRefreshAccessToken(async () => {
        const retryResponse = await fetch(
          `${
            process.env.NODE_ENV === 'development' ? process.env.NEXT_PUBLIC_API_BASE_PATH : window.location.origin
          }${endpoint}`,
          {
            method,
            credentials: 'include',
            headers: {
              'X-CSRFToken': CookieHelper.get('csrf') || '',
              Authorization: `Bearer ${CookieHelper.get('access_token')}`,
            },
            body: formData,
          },
        );
        if (retryResponse.status === 400) {
          await ErrorHelper.handleErrorNotification({ response: retryResponse });
          const error = new Error('Failed to submit form data after retry');
          (error as any).response = retryResponse;
          (error as any).status = retryResponse.status;
          throw error;
        }
        if (!(retryResponse.status >= 200 && retryResponse.status < 209)) {
          let responseMessage;
          try {
            const contentType = retryResponse.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              responseMessage = await retryResponse.json();
            } else {
              responseMessage = await retryResponse.text();
            }
          } catch (parseError) {
            responseMessage = 'Failed to parse error response';
          }
          const error = new Error('Failed to submit form data after retry');
          (error as any).response = retryResponse;
          (error as any).body = responseMessage;
          (error as any).status = retryResponse.status;
          throw error;
        }
      });
    } else if (response.status === 400) {
      await ErrorHelper.handleErrorNotification({ response });
      const error = new Error('Failed to submit form data');
      (error as any).response = response;
      (error as any).status = response.status;
      throw error;
    } else if (!(response.status >= 200 && response.status < 209)) {
      let responseMessage;
      try {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          responseMessage = await response.json();
        } else {
          responseMessage = await response.text();
        }
      } catch (parseError) {
        responseMessage = 'Failed to parse error response';
      }
      const error = new Error('Failed to submit form data');
      (error as any).response = response;
      (error as any).body = responseMessage;
      (error as any).status = response.status;
      throw error;
    }

    return await response.json();
  } catch (e) {
    await ErrorHelper.handleErrorNotification(e);
    throw e;
  }
};

const extractFileName = (path: string): string => {
  const lastPart = path.split('/').pop() || path;

  const underscoreIndex = lastPart.indexOf('_');
  if (underscoreIndex !== -1) {
    return lastPart.substring(underscoreIndex + 1).trim();
  }

  return lastPart.trim();
};

export const FilesHelper = {
  uploadFile,
  submitFormData,
  extractFileName,
};
